import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { tokens } from '../../global/theme/tokens';
import { DBUploadData, useConfig } from '../../api/api';
import { hasPermissions } from '../../auth/hasPermissions';
import {
  ChatEntityPicker,
  useAvailableEntityTypes,
} from '../chat-system/ChatEntityPicker';
import {
  AI_ENTITY_TYPES,
  referenceKey,
  rememberReferenceLabel,
} from './aiChatEntities';
import { AiReferenceChip, referenceLabel } from './AiReferenceChip';
import { AiProjectFilePicker } from './AiProjectFilePicker';
import ExoDialog from '../exo/ExoDialog';
import ExoExplorer from '../exo/ExoExplorer';
import { entryKind } from '../exo/project-file-explorer/explorerUtils';

// limits of POST /ai-chats/{chat}/messages and POST /files
const MAX_PROMPT_LENGTH = 10000;
const MAX_ATTACHMENTS = 10; // File ids: uploads + ExoExplorer
const MAX_PROJECT_FILES = 10;
const MAX_REFERENCES = 10;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const EXPLORER_MAX_SIZE_KB = 50 * 1024;

// Attachments come from three sources:
// 'upload' (local file, uploaded as PRIVATE file on send),
// 'explorer' (existing File from the ExoExplorer),
// 'project' (file in the project's network storage, referenced by path)
const isImageFile = (mimeType) => Boolean(mimeType?.startsWith('image/'));

// ref.addUploads(files) lets the panel hand over files dropped anywhere on it
export const AiChatComposer = forwardRef(function AiChatComposer(
  {
    pageContext,
    scopeProject,
    sentReferenceKeys,
    canWrite,
    isBusy,
    blocked,
    error,
    onClearError,
    onSend,
    focusKey,
  },
  ref,
) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();
  const canLinkEntities = useAvailableEntityTypes().length > 0;

  const [text, setText] = useState('');
  const [extraRefs, setExtraRefs] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attachMenuAnchor, setAttachMenuAnchor] = useState(null);
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [projectChooserOpen, setProjectChooserOpen] = useState(false);
  const [folderProject, setFolderProject] = useState(null);
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [dismissedKey, setDismissedKey] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [localError, setLocalError] = useState(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (focusKey) inputRef.current?.focus();
  }, [focusKey]);

  const contextKey = referenceKey(pageContext);
  const contextAllowed =
    pageContext &&
    hasPermissions(AI_ENTITY_TYPES[pageContext.type]?.permission, 'read');
  const contextSent = contextAllowed && sentReferenceKeys.has(contextKey);
  const contextActive =
    contextAllowed && !contextSent && dismissedKey !== contextKey;

  const references = [
    ...(contextActive ? [pageContext] : []),
    ...extraRefs.filter((ref) => referenceKey(ref) !== contextKey),
  ].slice(0, MAX_REFERENCES);

  const canSend = text.trim() && !isSending && !isBusy && !blocked;

  const fileCount = attachments.filter((a) => a.source !== 'project').length;
  const projectFileCount = attachments.length - fileCount;
  const canUseExplorer = hasPermissions('explorer', 'read');
  const canUseProjectFolder = hasPermissions('project', 'read');

  // keeps the first of duplicates and the per-kind limits of the API
  const addAttachments = (items) =>
    setAttachments((prev) => {
      const next = [...prev];
      items.forEach((item) => {
        if (next.some((a) => a.key === item.key)) return;
        const isProject = item.source === 'project';
        const count = next.filter((a) => (a.source === 'project') === isProject)
          .length;
        if (count < (isProject ? MAX_PROJECT_FILES : MAX_ATTACHMENTS))
          next.push(item);
      });
      return next;
    });

  const addUploads = (fileList) => {
    const incoming = Array.from(fileList || []);
    const tooLarge = incoming.filter((file) => file.size > MAX_FILE_SIZE);
    const stamp = Date.now();

    setLocalError(
      tooLarge.length
        ? t('Files larger than 5 MB cannot be attached: {{names}}', {
            names: tooLarge.map((file) => file.name).join(', '),
          })
        : null,
    );
    addAttachments(
      incoming
        .filter((file) => file.size <= MAX_FILE_SIZE)
        .map((file, index) => ({
          key: `upload:${stamp}:${index}`,
          source: 'upload',
          name: file.name,
          isImage: isImageFile(file.type),
          file,
        })),
    );
  };

  const addExplorerFiles = (selection) => {
    setExplorerOpen(false);
    addAttachments(
      [].concat(selection || []).map((file) => ({
        key: `explorer:${file.id}`,
        source: 'explorer',
        id: file.id,
        name: file.title || file.name || `#${file.id}`,
        isImage: isImageFile(file.mimeType),
      })),
    );
  };

  const addProjectFiles = (selection) => {
    setFolderPickerOpen(false);
    addAttachments(
      selection.map((file) => ({
        key: `project:${file.projectId}:${file.path}`,
        source: 'project',
        projectId: file.projectId,
        path: file.path,
        name: file.name,
        mimeType: file.mimeType,
        isImage: isImageFile(file.mimeType),
      })),
    );
  };

  const openProjectFolder = (project) => {
    setFolderProject(project);
    setFolderPickerOpen(true);
  };

  const handleAttachClick = (e) => {
    // with only one source available there is nothing to choose from
    if (!canUseExplorer && !canUseProjectFolder) fileInputRef.current?.click();
    else setAttachMenuAnchor(e.currentTarget);
  };

  useImperativeHandle(ref, () => ({ addUploads }));

  const uploadFiles = (files) =>
    new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('fileType', 'PRIVATE');
      files.forEach((file) => formData.append('files[]', file));

      DBUploadData({
        config,
        path: 'files',
        formData,
        onProgressChange: setUploadProgress,
        onResponse: (res) =>
          resolve((res.data?.data || res.data || []).map((file) => file.id)),
        onError: reject,
      });
    });

  const handleSend = async () => {
    if (!canSend) return;
    const prompt = text.trim();
    const previous = { text, extraRefs, attachments };
    const labeledRefs = references.map((ref) => ({
      ...ref,
      label: referenceLabel(ref, t),
    }));

    setIsSending(true);
    setLocalError(null);
    try {
      const uploads = attachments.filter((a) => a.source === 'upload');
      const uploadedIds = uploads.length
        ? await uploadFiles(uploads.map((a) => a.file))
        : [];
      labeledRefs.forEach(rememberReferenceLabel);

      setText('');
      setExtraRefs([]);
      setAttachments([]);
      await onSend({
        prompt,
        references: labeledRefs,
        attachmentIds: [
          ...attachments
            .filter((a) => a.source === 'explorer')
            .map((a) => a.id),
          ...uploadedIds,
        ],
        projectFiles: attachments
          .filter((a) => a.source === 'project')
          .map(({ projectId, path }) => ({ projectId, path })),
        localAttachments: attachments.map(
          ({ source, name, path, mimeType, isImage }) => ({
            source,
            name,
            path,
            mimeType,
            isImage,
          }),
        ),
      });
    } catch (err) {
      // keep what the user wrote, the error itself is shown by the panel
      setText(previous.text);
      setExtraRefs(previous.extraRefs);
      setAttachments(previous.attachments);
      if (err?.isAxiosError) setLocalError(t('File upload failed'));
    } finally {
      setIsSending(false);
      setUploadProgress(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!canWrite)
    return (
      <Box
        className="flex items-center gap-2 mx-4 mb-4 px-3 py-2 rounded-xl"
        sx={{ backgroundColor: colors.bgInput }}
      >
        <LockOutlinedIcon sx={{ fontSize: 18, opacity: 0.6 }} />
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {t('You only have read access to this AI chat.')}
        </Typography>
      </Box>
    );

  const shownError = localError || error;
  const iconButtonSx = {
    width: 32,
    height: 32,
    color: colors.text,
    opacity: 0.6,
    '&:hover': { opacity: 1 },
  };

  return (
    <Box className="flex flex-col gap-2 px-4 pb-4 pt-1">
      {(contextAllowed || extraRefs.length > 0) && (
        <Box className="flex flex-wrap gap-1.5">
          {contextAllowed && (
            <AiReferenceChip
              reference={pageContext}
              variant={
                contextSent
                  ? 'sent'
                  : contextActive
                    ? 'active'
                    : 'ghost'
              }
              tooltip={
                contextSent
                  ? t('Already known in this chat')
                  : contextActive
                    ? t('Current page is sent as context')
                    : t('Add current page as context')
              }
              onRemove={
                contextActive ? () => setDismissedKey(contextKey) : undefined
              }
              onClick={
                !contextActive && !contextSent
                  ? () => setDismissedKey(null)
                  : undefined
              }
            />
          )}
          {extraRefs.map((ref) => (
            <AiReferenceChip
              key={referenceKey(ref)}
              reference={ref}
              onRemove={() =>
                setExtraRefs((prev) =>
                  prev.filter((r) => referenceKey(r) !== referenceKey(ref)),
                )
              }
            />
          ))}
        </Box>
      )}

      {attachments.length > 0 && (
        <Box className="flex flex-wrap gap-1.5">
          {attachments.map((attachment) => (
            <AttachmentChip
              key={attachment.key}
              attachment={attachment}
              onRemove={() =>
                setAttachments((prev) =>
                  prev.filter((a) => a.key !== attachment.key),
                )
              }
            />
          ))}
        </Box>
      )}

      {uploadProgress !== null && (
        <LinearProgress
          variant="determinate"
          value={uploadProgress}
          sx={{
            height: 3,
            borderRadius: 2,
            backgroundColor: colors.bgInput,
            '& .MuiLinearProgress-bar': { backgroundColor: colors.info },
          }}
        />
      )}

      {shownError && (
        <Box
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
          sx={{
            backgroundColor: `${colors.error}15`,
            border: `1px solid ${colors.error}40`,
          }}
        >
          <Typography variant="caption" sx={{ flex: 1, color: colors.error }}>
            {shownError === 'error' ? t('Something went wrong') : shownError}
          </Typography>
          <IconButton
            size="small"
            onClick={() => {
              setLocalError(null);
              onClearError();
            }}
            sx={{ p: 0.25, color: colors.error }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      )}

      <Box
        className="flex flex-col rounded-2xl px-2 pt-2 pb-1.5"
        sx={{
          backgroundColor: colors.bgInput,
          border: '1px solid transparent',
          transition: '0.2s ease',
          '&:focus-within': { borderColor: `${colors.info}60` },
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={8}
          placeholder={t('Ask the AI...')}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="standard"
          inputProps={{ maxLength: MAX_PROMPT_LENGTH }}
          InputProps={{ disableUnderline: true }}
          sx={{
            '& .MuiInputBase-root': { px: 1, py: 0.5, fontSize: '0.9rem' },
            '& .MuiInputBase-input::placeholder': { opacity: 0.5 },
          }}
        />
        <Box className="flex items-center gap-0.5 mt-1">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              addUploads(e.target.files);
              e.target.value = '';
            }}
          />
          <Tooltip title={t('Attach file')}>
            <IconButton
              size="small"
              onClick={handleAttachClick}
              disabled={
                fileCount >= MAX_ATTACHMENTS &&
                (!canUseProjectFolder || projectFileCount >= MAX_PROJECT_FILES)
              }
              sx={iconButtonSx}
            >
              <AttachFileIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
          {canLinkEntities && (
            <Tooltip title={t('Link Entity')}>
              <IconButton
                size="small"
                onClick={() => setPickerOpen(true)}
                disabled={references.length >= MAX_REFERENCES}
                sx={iconButtonSx}
              >
                <AddLinkIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          <Box className="flex-1" />
          <Tooltip
            title={isBusy ? t('An answer is being generated...') : ''}
            disableInteractive
          >
            <span>
              <IconButton
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSend}
                disabled={!canSend}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: colors.info,
                  color: '#fff',
                  '&:hover': { backgroundColor: colors.info, opacity: 0.9 },
                  '&.Mui-disabled': {
                    backgroundColor: colors.bgInput,
                    color: colors.text,
                    opacity: 0.4,
                  },
                }}
              >
                {isSending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Menu
        anchorEl={attachMenuAnchor}
        open={Boolean(attachMenuAnchor)}
        onClose={() => setAttachMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            backgroundColor: colors.glass,
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            minWidth: 260,
          },
        }}
      >
        {canUseProjectFolder && (
          <MenuItem
            disabled={projectFileCount >= MAX_PROJECT_FILES}
            onClick={() => {
              setAttachMenuAnchor(null);
              if (scopeProject) openProjectFolder(scopeProject);
              else setProjectChooserOpen(true);
            }}
          >
            <ListItemIcon>
              <FolderOpenIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t('From project folder')}
              secondary={scopeProject ? scopeProject.label : t('Choose project...')}
              secondaryTypographyProps={{ noWrap: true }}
            />
          </MenuItem>
        )}
        {canUseExplorer && (
          <MenuItem
            disabled={fileCount >= MAX_ATTACHMENTS}
            onClick={() => {
              setAttachMenuAnchor(null);
              setExplorerOpen(true);
            }}
          >
            <ListItemIcon>
              <PermMediaOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('From ExoExplorer')} />
          </MenuItem>
        )}
        <MenuItem
          disabled={fileCount >= MAX_ATTACHMENTS}
          onClick={() => {
            setAttachMenuAnchor(null);
            fileInputRef.current?.click();
          }}
        >
          <ListItemIcon>
            <UploadFileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('Upload from computer')} />
        </MenuItem>
      </Menu>

      <ExoDialog open={explorerOpen} onClose={() => setExplorerOpen(false)}>
        {explorerOpen && (
          <ExoExplorer
            multiSelect
            mediaType="DOCUMENT"
            maxItems={MAX_ATTACHMENTS - fileCount}
            maxSizeKB={EXPLORER_MAX_SIZE_KB}
            onSelect={addExplorerFiles}
            onClose={() => setExplorerOpen(false)}
          />
        )}
      </ExoDialog>

      <ChatEntityPicker
        open={projectChooserOpen}
        allowedTypes={['projects']}
        title={t('Choose project')}
        onClose={() => setProjectChooserOpen(false)}
        onSelect={(project) => {
          setProjectChooserOpen(false);
          openProjectFolder({ id: project.id, label: project.label });
        }}
      />

      <AiProjectFilePicker
        open={folderPickerOpen}
        projectId={folderProject?.id}
        projectLabel={folderProject?.label}
        maxItems={MAX_PROJECT_FILES - projectFileCount}
        onClose={() => setFolderPickerOpen(false)}
        onSelect={addProjectFiles}
      />

      <ChatEntityPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(ref) => {
          setExtraRefs((prev) =>
            prev.some((r) => referenceKey(r) === referenceKey(ref))
              ? prev
              : [...prev, ref],
          );
          setPickerOpen(false);
        }}
      />
    </Box>
  );
});

const AttachmentChip = ({ attachment, onRemove }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const kind =
    attachment.source === 'project'
      ? entryKind({ name: attachment.name, mimeType: attachment.mimeType })
      : {
          Icon: attachment.isImage ? ImageIcon : InsertDriveFileIcon,
          color: colors.text,
        };
  const sourceLabel = {
    project: `${t('Project folder')}: ${attachment.path}`,
    explorer: 'ExoExplorer',
    upload: t('Upload from computer'),
  }[attachment.source];

  return (
    <Tooltip title={sourceLabel} disableInteractive>
      <Box
        className="flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-lg min-w-0"
        sx={{ backgroundColor: colors.card, border: `1px solid ${colors.bgInput}` }}
      >
        {attachment.source === 'project' && (
          <FolderOpenIcon sx={{ fontSize: 13, opacity: 0.5 }} />
        )}
        <kind.Icon sx={{ fontSize: 14, color: kind.color, opacity: 0.8 }} />
        <Typography variant="caption" noWrap sx={{ maxWidth: 140 }}>
          {attachment.name}
        </Typography>
        <IconButton size="small" onClick={onRemove} sx={{ p: 0.25, opacity: 0.6 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Tooltip>
  );
};
